import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Selectors } from "../../Everou/_reducers/app.reducer";
import { OrganizationsActions } from "../../Organizations/_actions/OrganizationsActions";
import { Organization } from "../../Organizations/_models/Organization";

// Loads the user's organizations and resolves the one the PMS modules
// operate on (explicit choice from PMSActiveOrganizationStore, else the
// first; null for users without organization → personal/legacy scope).
// `isResolving` stays true until the list has loaded successfully at least
// once: a failed load must not be mistaken for "no organizations" (the
// modules would silently operate on the personal scope). `hasFailed` tells
// that case apart for consumers that prefer a fallback to nothing at all —
// the header branding, say.
export function useActiveOrganization(): {
    organizations: Organization[];
    activeOrganization: Organization | null;
    isResolving: boolean;
    hasFailed: boolean;
} {
    const dispatch = useDispatch();

    const organizations: Organization[] = useSelector(
        Selectors.getOrganizations,
    );
    const choiceUid: string | null = useSelector(
        Selectors.getPMSActiveOrganizationChoice,
    );
    const hasLoaded: boolean = useSelector(Selectors.getOrganizationsHasLoaded);
    const hasFailed: boolean = useSelector(Selectors.getOrganizationsHasFailed);
    const isRequesting: boolean = useSelector(
        Selectors.getOrganizationsIsRequestingList,
    );

    // The list is shared app-wide and changes rarely, so fetch it only when
    // nobody has it or is already asking for it: the hook runs in the PMS
    // shell and in several modules, which otherwise fired the same request
    // two or three times per load. A previous failure leaves both flags
    // false, so the next mount retries on its own. Screens that must see
    // fresh data after mutating it (the Organizations module) dispatch
    // listOrganizations themselves.
    useEffect(() => {
        if (hasLoaded || isRequesting) return;
        dispatch(OrganizationsActions.listOrganizations());
        // Re-running on every flag change would re-fire the request the
        // moment a failure clears isRequesting; mount (and genuine flag
        // transitions) is enough.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    const activeOrganization =
        organizations.find((organization) => organization.uid === choiceUid) ??
        organizations[0] ??
        null;

    return {
        organizations,
        activeOrganization,
        isResolving: !hasLoaded,
        hasFailed,
    };
}
